import * as React from "react";
import {useState} from "react";
import {TimeInput} from "@/shared/ui/primitives/TimeInput";
import {formatDate, formatTime, getCurrentISOOffset, getISOTime, parseTimeInput, Temporal} from "@/shared/utils/datefunctions";

interface TimeInputWithShiftDropdownProps {
	id: string;
	/**
	 * The endpoint's current date+time as a full date-time, or `null` when the time is empty. Typed times keep
	 * this date-time's day (today when no value is set yet); the dropdown shortcuts ("now"/"last end") carry
	 * their own day — "last end" may lie on an earlier calendar day.
	 */
	value: Temporal.ZonedDateTime | null;
	/** Fired on every user edit with the new full date+time, or null when the time was cleared. */
	onValueChange: (value: Temporal.ZonedDateTime | null) => void;
	className: string;
	disabled: boolean;
	/**
	 * The most recent record's end for the "last end" shortcut. Omit to hide the shortcut entirely;
	 * pass null to show it disabled (the legacy recording-dialog behavior).
	 */
	lastRecordEnd?: Temporal.ZonedDateTime | null;
}

/** The committed `HH:mm` time applied onto `base`'s day (falling back to today), as a full date-time. */
function atTime(base: Temporal.ZonedDateTime | null, time: string | null): Temporal.ZonedDateTime | null {
	const parsed = parseTimeInput(time);
	if (parsed) {
		const anchor = base ?? Temporal.Now.zonedDateTimeISO(getCurrentISOOffset());
		return anchor.with({hour: parsed.hour, minute: parsed.minute, second: 0, millisecond: 0});
	}
	return base;
}

/** `(HH:mm)`, plus the date when the last end is not on the same calendar day as `now`; empty while unavailable. */
function lastEndLabel(lastEnd: Temporal.ZonedDateTime | null, now: Temporal.ZonedDateTime): string {
	if (!lastEnd) {
		return "";
	}
	const dateSuffix = formatDate(now) !== formatDate(lastEnd) ? `, ${formatDate(lastEnd)}` : "";
	return `(${formatTime(lastEnd)}${dateSuffix})`;
}

/**
 * A {@link TimeInput} with a focus-triggered dropdown offering "shift to now" and, optionally, "shift to
 * last end" shortcuts — the recording-dialog pattern shared across working-time, project-time and drive-log
 * entry. Purely presentational: it reports every edit via {@link TimeInputWithShiftDropdownProps.onValueChange}.
 */
export function TimeInputWithShiftDropdown({
	id,
	value,
	onValueChange,
	className,
	disabled,
	lastRecordEnd
}: Readonly<TimeInputWithShiftDropdownProps>) {
	const [open, setOpen] = useState(false);
	const [now, setNow] = useState<Temporal.ZonedDateTime>(() => Temporal.Now.zonedDateTimeISO(getCurrentISOOffset()));
	const lastEnd = lastRecordEnd ?? null;

	const shift = (event: React.MouseEvent<HTMLAnchorElement>, dateTime: Temporal.ZonedDateTime | null) => {
		event.preventDefault();
		if (dateTime) {
			onValueChange(dateTime);
		}
		setOpen(false);
	};

	return (
		<div
			className="dropdown"
			// focusout bubbles, so this closes the dropdown whenever focus leaves the input *and* the
			// shortcut anchors — including tabbing off an anchor, which a blur handler on the input alone
			// would miss.
			onBlur={event => {
				if (!event.currentTarget.contains(event.relatedTarget)) {
					setOpen(false);
				}
			}}
		>
			<TimeInput
				id={id}
				value={value ? getISOTime(value) : null}
				onValueChange={time => onValueChange(atTime(value, time))}
				className={className}
				disabled={disabled}
				onFocus={() => {
					setNow(Temporal.Now.zonedDateTimeISO(getCurrentISOOffset()));
					setOpen(true);
				}}
			/>
			<ul className="dropdown-menu time-shift-dropdown" style={{display: open ? "block" : undefined}}>
				{lastRecordEnd !== undefined && (
					<li className={lastEnd ? undefined : "disabled"}>
						<a href="#" tabIndex={0} className="shift-time-entries" onClick={event => shift(event, lastEnd)}>
							{messages["recording.lastEnd"]}<span>{lastEndLabel(lastEnd, now)}</span>
						</a>
					</li>
				)}
				<li>
					<a href="#" tabIndex={0} className="shift-time-entries" onClick={event => shift(event, now)}>
						{messages["recording.now"]}<span>{`(${formatTime(now)})`}</span>
					</a>
				</li>
			</ul>
		</div>
	);
}

export type {TimeInputWithShiftDropdownProps};
